/* jp-theme.js — small global enhancements (no jQuery, no frameworks).
   1) Mirrors Dawn's #cart-icon-bubble count into [data-jp-cart-count]
      (mobile header + bottom nav) via MutationObserver — survives any
      Dawn AJAX cart update without touching Dawn internals.
   2) Injects the cart free-delivery progress bar; ports Dawn's StickyHeader.
   (Drawer + search now open via [data-jp-open] — handled by jp-layers.js.) */
(() => {
  if (window.__jpThemeInit) return;
  window.__jpThemeInit = true;
  const pubSubEvents = () => (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);
  const syncCount = () => {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
    const count = bubble ? bubble.textContent.trim() : '';
    document.querySelectorAll('[data-jp-cart-mirror]').forEach((wrap) => {
      let b = wrap.querySelector('.cart-count-bubble');
      if (count) {
        if (!b) {
          b = document.createElement('span');
          b.className = 'cart-count-bubble';
          b.innerHTML = '<span aria-hidden="true" data-jp-cart-count></span>';
          wrap.appendChild(b);
        }
        b.querySelector('[data-jp-cart-count]').textContent = count;
      } else if (b) {
        b.remove();
      }
    });
  };

  /* ---- trust-bar announcement rotation (2–3 messages, slow crossfade) ----
     Static with one message; static under prefers-reduced-motion; pauses on
     hover/focus so links stay clickable. */
  const initAnnounce = () => {
    document.querySelectorAll('[data-jp-announce]').forEach((bar) => {
      if (bar.dataset.jpAnnounceReady) return;
      bar.dataset.jpAnnounceReady = 'true';
      const items = [...bar.querySelectorAll('[data-jp-announce-item]')];
      if (items.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const interval = parseInt(bar.dataset.interval, 10) || 8000;
      let i = 0;
      let timer = null;
      // seed inert from the server-rendered is-active item (before rotation starts)
      items.forEach((el) => { el.inert = !el.classList.contains('is-active'); });
      const show = (n) => {
        items.forEach((el, idx) => {
          const on = idx === n;
          el.classList.toggle('is-active', on);
          el.inert = !on; // pull inactive links out of focus + AT immediately (CSS visibility covers steady-state / no-JS)
        });
      };
      const start = () => {
        if (timer) return;
        timer = window.setInterval(() => {
          i = (i + 1) % items.length;
          show(i);
        }, interval);
      };
      const stop = () => { window.clearInterval(timer); timer = null; };
      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', start);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', start);
      start();
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initAnnounce();
    document.addEventListener('shopify:section:load', () => initAnnounce());

    const src = document.getElementById('cart-icon-bubble');
    if (src) new MutationObserver(syncCount).observe(src, { childList: true, subtree: true });
    syncCount();

    /* persona grid + drawer now live in snippets/jp-drawer.liquid (server-rendered) */

    /* ---- Phase 6a · free-delivery progress bar in the cart drawer ---- */
    const shipTpl = document.querySelector('template[data-jp-shipbar-template]');
    if (shipTpl) {
      const threshold = parseInt(shipTpl.dataset.thresholdCents, 10) || 0;
      const currency = shipTpl.dataset.currency || 'GBP';
      const lang = document.documentElement.lang || 'en';
      let lastTotal = parseInt(shipTpl.dataset.totalCents, 10) || 0;
      const fmt = (cents) => {
        try {
          return new Intl.NumberFormat(lang, { style: 'currency', currency, minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
        } catch { return (cents / 100).toFixed(2); }
      };
      const renderBar = (total) => {
        lastTotal = total;
        const drawer = document.querySelector('cart-drawer .drawer__inner');
        if (!drawer || threshold <= 0) return;
        if (drawer.querySelector('.drawer__inner-empty') || total <= 0) {
          drawer.querySelector('.jp-shipbar')?.remove();
          return;
        }
        let bar = drawer.querySelector('.jp-shipbar');
        if (!bar) {
          const frag = shipTpl.content.cloneNode(true);
          const header = drawer.querySelector('.drawer__header');
          if (header) header.after(frag);
          else drawer.prepend(frag);
          bar = drawer.querySelector('.jp-shipbar');
        }
        if (!bar) return;
        const reached = total >= threshold;
        bar.classList.toggle('is-reached', reached);
        bar.querySelector('.jp-shipbar__msg').innerHTML = reached
          ? shipTpl.dataset.msgReached
          : shipTpl.dataset.msgAway.replace('[amount]', '<b>' + fmt(threshold - total) + '</b>');
        bar.querySelector('.jp-shipbar__fill').style.width = Math.min(100, (total / threshold) * 100) + '%';
      };
      let shipTimer;
      const refreshBar = () => {
        window.clearTimeout(shipTimer);
        shipTimer = setTimeout(() => {
          fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
            .then((r) => r.json())
            .then((c) => renderBar(c.total_price))
            .catch(() => renderBar(lastTotal));
        }, 150);
      };
      renderBar(lastTotal);
      const shipEvents = pubSubEvents();
      if (typeof subscribe === 'function' && shipEvents?.cartUpdate) {
        subscribe(shipEvents.cartUpdate, refreshBar);
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) new MutationObserver(refreshBar).observe(bubble, { childList: true, subtree: true });
    }

    /* ---- P2.4 · "Completes your kit" drawer cross-sell — injected + re-applied like the ship bar ---- */
    const kitTpl = document.querySelector('template[data-jp-kit-template]');
    if (kitTpl) {
      const kitRoot = window.Shopify?.routes?.root || '/';
      const renderKit = () => {
        const drawer = document.querySelector('cart-drawer .drawer__inner');
        if (!drawer) return;
        let kit = drawer.querySelector('[data-jp-kit]');
        const footer = drawer.querySelector('.drawer__footer');
        if (drawer.querySelector('.drawer__inner-empty') || !footer) { if (kit) kit.remove(); return; }
        if (!kit) {
          footer.before(kitTpl.content.cloneNode(true));
          kit = drawer.querySelector('[data-jp-kit]');
        }
        if (!kit) return;
        /* dedup: hide any row whose product is already in the cart; hide the block if none remain */
        fetch(`${kitRoot}cart.js`).then((r) => r.json()).then((c) => {
          const ids = new Set((c.items || []).map((i) => String(i.product_id)));
          let shown = 0;
          kit.querySelectorAll('[data-jp-kit-row]').forEach((row) => {
            const hide = ids.has(row.dataset.productId);
            row.hidden = hide;
            if (!hide) shown += 1;
          });
          kit.hidden = shown === 0;
        }).catch(() => {});
      };
      let kitTimer;
      const refreshKit = () => { window.clearTimeout(kitTimer); kitTimer = setTimeout(renderKit, 150); };
      renderKit();
      const kitEvents = pubSubEvents();
      if (typeof subscribe === 'function' && kitEvents?.cartUpdate) {
        subscribe(kitEvents.cartUpdate, refreshKit);
      }
      const kitBubble = document.getElementById('cart-icon-bubble');
      if (kitBubble) new MutationObserver(refreshKit).observe(kitBubble, { childList: true, subtree: true });
    }

    /* ---- Phase 3 · exact per-line fulfilment + mixed-cart notice -----------------
       Dawn cart files stay pristine. Liquid emits a header-owned payload; after every
       cart change we refetch only that section, clear old messages immediately, and
       inject the new facts into drawer + /cart rows by selected variant id. */
    let fulfilmentTpl = document.querySelector('template[data-jp-cart-fulfilment-template]');
    if (fulfilmentTpl) {
      const clearFulfilment = () => {
        document.querySelectorAll('[data-jp-cart-fulfilment-injected]').forEach((el) => el.remove());
      };
      const injectFulfilment = () => {
        clearFulfilment();
        if (!fulfilmentTpl) return;
        const sourceSummary = fulfilmentTpl.content.querySelector('.jp-cart-mixed');
        const summaryHosts = [
          document.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper'),
          document.querySelector('#main-cart-items .js-contents > .cart-items'),
        ].filter(Boolean);
        if (sourceSummary) summaryHosts.forEach((host) => {
          const summary = sourceSummary.cloneNode(true);
          summary.dataset.jpCartFulfilmentInjected = '1';
          host.before(summary);
        });
        document.querySelectorAll('cart-drawer .cart-item, #main-cart-items .cart-item').forEach((row) => {
          const variantId = row.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId;
          const source = variantId && fulfilmentTpl.content.querySelector(`[data-jp-cart-line="${CSS.escape(variantId)}"] .jp-cart-fulfil`);
          const details = row.querySelector('.cart-item__details');
          if (!source || !details) return;
          const line = source.cloneNode(true);
          line.dataset.jpCartFulfilmentInjected = '1';
          const name = details.querySelector('.cart-item__name');
          if (name) name.after(line);
          else details.prepend(line);
        });
      };
      let fulfilmentTimer = 0;
      let fulfilmentController = null;
      const refreshFulfilment = () => {
        window.clearTimeout(fulfilmentTimer);
        clearFulfilment();
        fulfilmentTimer = window.setTimeout(() => {
          const sectionId = fulfilmentTpl?.dataset.sectionId;
          if (!sectionId) return;
          fulfilmentController?.abort();
          fulfilmentController = new AbortController();
          const base = window.location.pathname;
          fetch(`${base}?section_id=${encodeURIComponent(sectionId)}`, { signal: fulfilmentController.signal })
            .then((response) => { if (!response.ok) throw new Error('http'); return response.text(); })
            .then((html) => {
              const next = new DOMParser().parseFromString(html, 'text/html').querySelector('template[data-jp-cart-fulfilment-template]');
              if (!next) throw new Error('missing-payload');
              fulfilmentTpl.replaceWith(next);
              fulfilmentTpl = next;
              injectFulfilment();
            })
            .catch((error) => { if (error.name !== 'AbortError') clearFulfilment(); });
        }, 120);
      };
      injectFulfilment();
      const fulfilmentEvents = pubSubEvents();
      if (typeof subscribe === 'function' && fulfilmentEvents?.cartUpdate) {
        subscribe(fulfilmentEvents.cartUpdate, refreshFulfilment);
      }
      const fulfilmentBubble = document.getElementById('cart-icon-bubble');
      if (fulfilmentBubble) new MutationObserver(refreshFulfilment).observe(fulfilmentBubble, { childList: true, subtree: true });
    }

    /* ---- P2.4 · kit-row AJAX add — keep the shopper IN the drawer (plain POST full-reloads; the form
       stays a working no-JS fallback). Reuses Dawn's cart-drawer.renderContents() + section render. ---- */
    if (!document.body.dataset.jpKitAjax) {
      document.body.dataset.jpKitAjax = '1';
      document.addEventListener('submit', (e) => {
        const form = e.target.closest && e.target.closest('.jp-kit__add-f');
        if (!form) return;
        const btn = form.querySelector('.jp-kit__add');
        if (btn && btn.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (!cart || typeof cart.renderContents !== 'function') return; // no drawer element → let the native POST proceed
        e.preventDefault();
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const body = new FormData(form);
        if (typeof cart.getSectionsToRender === 'function') {
          body.append('sections', cart.getSectionsToRender().map((s) => s.id).join(','));
          body.append('sections_url', window.location.pathname);
        }
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        if (btn) btn.setAttribute('aria-disabled', 'true');
        fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, body })
          .then((r) => r.json())
          .then((res) => {
            if (res.status) { form.submit(); return; } // e.g. sold-out race → fall back to the native add (honest error page)
            cart.renderContents(res); // re-renders the drawer sections + opens it; the cart-icon-bubble mutation re-injects the kit block
            const kitAddEvents = pubSubEvents();
            if (typeof publish === 'function' && kitAddEvents?.cartUpdate) {
              publish(kitAddEvents.cartUpdate, { source: 'jp-kit-row', productVariantId: body.get('id'), cartData: res });
            }
          })
          .catch(() => { form.submit(); })
          .finally(() => { if (btn) btn.removeAttribute('aria-disabled'); });
      });
    }

    /* drawer + search open via [data-jp-open] — handled by jp-layers.js */
  });
})();

/* ============================================================
   E2 · StickyHeader — ported verbatim from Dawn 15.4.1
   sections/header.liquid {% javascript %} (L316-426), because we
   render jp-header instead of Dawn's header section. Contract:
   targets .section-header wrapper; classes styled by base.css.
   ============================================================ */
if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = document.querySelector('.section-header');
      if (!this.header) return;
      this.headerIsAlwaysSticky =
        this.getAttribute('data-sticky-type') === 'always' ||
        this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};
      this.setHeaderHeight();
      window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));
      if (this.headerIsAlwaysSticky) this.header.classList.add('shopify-section-header-sticky');
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');
      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
      this.createObserver();
    }
    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }
    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }
    createObserver() {
      const observer = new IntersectionObserver((entries, obs) => {
        this.headerBounds = entries[0].intersectionRect;
        obs.disconnect();
      });
      observer.observe(this.header);
    }
    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;
      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => { this.preventReveal = false; }, 66);
          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }
      this.currentScrollTop = scrollTop;
    }
    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeSearchModal();
    }
    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }
    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }
    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      if (this.searchModal && typeof this.searchModal.close === 'function') this.searchModal.close(false);
    }
  }
  customElements.define('sticky-header', StickyHeader);
}

/* ============================================================
   E3 · Analytics — GA4-ready dataLayer events (no-op without GTM)
   ============================================================ */
(() => {
  if (window.__jpAnalyticsInit) return;
  window.__jpAnalyticsInit = true;
  const push = (event, data) => {
    const privacy = window.JapanScissors && window.JapanScissors.privacy;
    if (!privacy || !privacy.analyticsAllowed()) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event }, data));
  };
  /* S31 — conversion events. Features dispatch a `jp:track` CustomEvent; only this helper owns dataLayer. */
  document.addEventListener('jp:track', (e) => { const { event, ...data } = e.detail || {}; if (event) push(event, data); });
  document.addEventListener('click', (e) => {
    const pill = e.target.closest('.jp-drawer__pill');
    if (pill) push('jp_drawer_quick_click', { label: pill.textContent.trim() });
    const tab = e.target.closest('[data-jp-ftab]');
    if (tab) push('jp_ftab_switched', { label: tab.textContent.trim() });
    const contact = e.target.closest('.jp-pp__contact'); // quick-contact tap (S4)
    if (contact) push('jp_quick_contact', { channel: (contact.getAttribute('href') || '').indexOf('wa.me') > -1 ? 'whatsapp' : 'contact' });
  });
  document.addEventListener('submit', (e) => { // cart "completes your kit" cross-sell add (S8)
    if (e.target.closest('.jp-kit__add-f') && !e.target.closest('[data-jp-cartx]')) push('jp_kit_add', {}); // cart-page copy fires via jp:track — avoid double-count
  });
  let cpcTimer;
  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-cpc-price], [data-cpc-cuts], [data-cpc-years]')) return;
    const root = e.target.closest('[data-jp-cpc]');
    window.clearTimeout(cpcTimer);
    cpcTimer = setTimeout(() => {
      push('jp_cpc_used', {
        price: root?.querySelector('[data-cpc-price]')?.value,
        cuts: root?.querySelector('[data-cpc-cuts]')?.value,
        years: root?.querySelector('[data-cpc-years]')?.value,
      });
    }, 900);
  });
})();
